import { useSessionStorage } from '@cheqroom/hooks';
import { UseQueryOptions } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { DiscoveryResponse, useDoDiscovery } from '../../api/discovery.api';
import { useDoInitiate, useDoInitiateWithSso } from '../../api/initiate.api';
import { Step } from '../../App';

export const useInitiateAuthentication = () => {
	const navigateTo = useNavigate();

	return useDoInitiate({
		onSuccess: () => {
			navigateTo(Step.CONFIRM_EMAIL);
		},
	});
};

export const useInitiateSsoAuthentication = () => {
	const navigateTo = useNavigate();

	return useDoInitiateWithSso({
		onSuccess: (response) => {
			if (response.redirectUrl) {
				window.location.replace(response.redirectUrl);
				return;
			}

			navigateTo(Step.CONFIRM_EMAIL);
		},
	});
};

export const useDiscoverWorkspaces = (options?: UseQueryOptions<DiscoveryResponse>) => {
	const navigateTo = useNavigate();

	// EmailStep is reached whenever the user starts (or restarts) identifying an email - including
	// "go back"/"start over" paths after picking an org. Any org scoped from a previous attempt is
	// stale here; without this, a multi-org user who restarts never sees the org picker again (the
	// stale id scopes discovery to just the one org they picked last time, which no longer looks
	// ambiguous).
	// Via the hook's clear (not a raw removeItem): StorageProvider keeps its own in-memory copy and
	// only drops it on the storage event the hook dispatches.
	const [, , clearOrganisationInSession] = useSessionStorage<string>('organisation');
	useEffect(() => {
		clearOrganisationInSession();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return useDoDiscovery(
		{},
		{
			onSuccess: (data: DiscoveryResponse) => {
				options?.onSuccess?.(data);

				navigateTo(Step.SELECT_WORKSPACE);
			},
		}
	);
};
